import { router, usePage } from '@inertiajs/react'
import { useEffect, useState, type ReactElement } from 'react'
import { CircleHelpIcon } from 'lucide-react'
import AppLayout from '@/layouts/AppLayout'
import { ScreenGuideDialog } from '@/components/ScreenGuideDialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { useScreenGuide } from '@/hooks/useScreenGuide'
import { EMPTY_KG, useKnowledgeGraphHistory } from '@/hooks/useKnowledgeGraphHistory'
import { useKnowledgeGraphAutosave } from '@/hooks/useKnowledgeGraphAutosave'
import type { SharedData } from '@/types/shared'
import { onboardingContent } from './onboardingContent'
import type {
  BusinessFlowLane,
  EntityRole
} from '@/types/knowledge_graph'
import type { ProjectNav } from '@/types/project'
import type { FailedPhase, RequirementsSpecificationDetail } from '@/types/requirements_specification'
import type { UnresolvedQuestionItem } from '@/types/requirements_quality'
import { ConceptsTab } from './tabs/ConceptsTab'
import { DemandsTab } from './tabs/DemandsTab'
import { RequirementsTab } from './tabs/RequirementsTab'
import { FlowsTab } from './tabs/FlowsTab'
import { MatrixTab } from './tabs/MatrixTab'
import { QuestionsTab } from './tabs/QuestionsTab'

// =========================================================
// Types
// =========================================================

interface Props {
  project_nav: ProjectNav
  requirements_specification_detail: RequirementsSpecificationDetail
  unresolved_questions: UnresolvedQuestionItem[] | null
  business_flow_lanes: BusinessFlowLane[] | null
  conflict?: boolean
  failed_phases?: FailedPhase[]
  entity_role_labels?: Partial<Record<EntityRole, string>>
  source_note_id?: number | null
}

// =========================================================
// Constants
// =========================================================

const KG_TABS = [
  { key: 'concepts', label: '概念モデル' },
  { key: 'demands', label: '要求' },
  { key: 'requirements', label: '要件' },
  { key: 'flows', label: '業務フロー' },
  { key: 'matrix', label: '権限マトリクス' },
  { key: 'questions', label: '確認ポイント' }
]

// =========================================================
// Page component
// =========================================================

export default function RequirementsSpecificationsShow ({
  project_nav: project,
  requirements_specification_detail: requirementsSpecification,
  unresolved_questions: unresolvedQuestions,
  business_flow_lanes: businessFlowLanes,
  conflict = false,
  failed_phases: failedPhases = [],
  entity_role_labels: entityRoleLabels = {},
  source_note_id: sourceNoteId = null
}: Props): ReactElement {
  const rdBase = `/projects/${project.id}/requirements_specification`

  const { kg, dispatch } = useKnowledgeGraphHistory(requirementsSpecification.draft_knowledge_graph ?? EMPTY_KG)

  const [activeTab, setActiveTab] = useState('concepts')
  const [flowViewMode, setFlowViewMode] = useState<'steps' | 'lanes'>('steps')
  const [failedPhasesDismissed, setFailedPhasesDismissed] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [retryConfirmOpen, setRetryConfirmOpen] = useState(false)

  function handleConfirm (): void {
    setConfirmOpen(false)
    setConfirming(true)
    router.post(`/projects/${project.id}/requirements_specification/audit`)
  }

  function handleRetryAnalyze (): void {
    if (sourceNoteId == null) return
    setRetryConfirmOpen(false)
    setRetrying(true)
    router.post(`/projects/${project.id}/source_notes/${sourceNoteId}/retry_analyze`)
  }

  // プログレス計算
  const demands = kg.demands ?? []
  const requirements = kg.requirements ?? []
  const allItems = demands.length + requirements.length
  const approvedItems =
    demands.filter((d) => d.review_status === 'approved').length +
    requirements.filter((r) => r.review_status === 'approved').length
  const checkpointCount = (unresolvedQuestions ?? []).length

  const { current_user: currentUser } = usePage<SharedData>().props
  const guide = useScreenGuide(
    'requirements_specification',
    currentUser?.onboardings ?? {},
    currentUser?.account_type === 'demo'
  )

  function handleCloseGuide (): void {
    guide.close()
  }

  function handleOpenHelp (): void {
    guide.openHelp()
  }

  // 自動保存
  const { isDirty, showSaved, saveConflict, saveFailed, triggerSave } = useKnowledgeGraphAutosave({
    url: `${rdBase}/draft`,
    kg,
    initialLockVersion: requirementsSpecification.kg_lock_version ?? 0
  })

  useEffect(() => {
    function handleKeyDown (e: KeyboardEvent): void {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'z') return
      e.preventDefault()
      dispatch({ type: e.shiftKey ? 'REDO' : 'UNDO' })
      triggerSave()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [triggerSave])

  // =========================================================
  // Render
  // =========================================================

  return (
    <AppLayout project={project} mainClassName='overflow-hidden flex flex-col'>
      <ScreenGuideDialog
        open={guide.open}
        onClose={handleCloseGuide}
        title={onboardingContent.title}
        sections={onboardingContent.sections}
      />
      <Tabs value={activeTab} onValueChange={setActiveTab} className='flex flex-col flex-1 overflow-hidden'>
        {/* ページヘッダー */}
        <div style={{ padding: '20px 32px 0', background: 'var(--bp-bg)', flexShrink: 0 }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            marginBottom: 16
          }}
          >
            <div>
              <div style={{
                fontSize: 20,
                fontWeight: 700,
                letterSpacing: '-.03em',
                color: 'var(--bp-ink)',
                marginBottom: 4
              }}
              >
                要件定義
              </div>
              <div style={{ fontSize: 13, color: 'var(--bp-ink3)' }}>
                {project.name} — 内容を確認・修正して、品質チェックを依頼してください
              </div>
            </div>

            {/* 右上: ヘルプ + リスク + プログレス + 確定ボタン */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
              <button
                onClick={handleOpenHelp}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '5px 10px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--bp-ink3)',
                  cursor: 'pointer',
                  fontFamily: 'var(--bp-font-sans)',
                  whiteSpace: 'nowrap'
                }}
              >
                <CircleHelpIcon size={14} /> 使い方
              </button>
              {checkpointCount > 0 && (
                <button
                  onClick={() => setActiveTab('questions')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '5px 10px',
                    background: 'var(--bp-warning-pale)',
                    border: '1px solid var(--bp-warning-border)',
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'var(--bp-warning)',
                    cursor: 'pointer',
                    fontFamily: 'var(--bp-font-sans)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  確認ポイント {checkpointCount}件
                </button>
              )}
              {allItems > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 120,
                    height: 6,
                    background: 'var(--bp-border)',
                    borderRadius: 3,
                    overflow: 'hidden'
                  }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${Math.round((approvedItems / allItems) * 100)}%`,
                        background: 'var(--bp-ok)',
                        borderRadius: 3
                      }}
                    />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--bp-ink2)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {approvedItems} / {allItems} 確認済
                  </div>
                </div>
              )}
              {requirementsSpecification.status !== 'risk_checked' && (
                <button
                  onClick={() => setConfirmOpen(true)}
                  disabled={confirming}
                  style={{
                    padding: '9px 20px',
                    background: 'var(--bp-accent)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: confirming ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                    letterSpacing: '-.01em',
                    opacity: confirming ? 0.7 : 1,
                    fontFamily: 'var(--bp-font-sans)'
                  }}
                >
                  {confirming ? '処理中...' : '品質チェックを依頼する →'}
                </button>
              )}
            </div>
          </div>

          {/* タブバー */}
          <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--bp-border)' }}>
            <TabsList className='border-b-0 gap-0'>
              {KG_TABS.map((tab) => {
                const count =
                tab.key === 'concepts'
                  ? (kg.concept_model?.entities ?? []).length
                  : tab.key === 'demands'
                    ? demands.length
                    : tab.key === 'requirements'
                      ? requirements.length
                      : tab.key === 'questions'
                        ? checkpointCount
                        : null
                return (
                  <TabsTrigger key={tab.key} value={tab.key}>
                    {tab.label}
                    {count !== null && count > 0 && (
                      <span style={{
                        display: 'inline-block',
                        marginLeft: 5,
                        padding: '1px 6px',
                        borderRadius: 8,
                        background: tab.key === 'questions' ? 'var(--bp-warning)' : 'var(--bp-surface2)',
                        color: tab.key === 'questions' ? '#fff' : 'var(--bp-ink3)',
                        fontSize: 10,
                        fontWeight: 700
                      }}
                      >
                        {count}
                      </span>
                    )}
                  </TabsTrigger>
                )
              })}
            </TabsList>

            {/* 保存ステータス */}
            {showSaved && (
              <div
                className='fixed bottom-4 right-4 z-50 flex items-center gap-1.5 bg-slate-800/90 text-white text-xs px-3 py-1.5 rounded-full shadow-(--shadow-md) pointer-events-none'
                style={{ animation: 'saved-toast 2s ease forwards' }}
              >
                <span className='text-emerald-400'>✓</span> 保存しました
              </div>
            )}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 8 }}>
              {saveConflict && (
                <span style={{ fontSize: 11, color: 'var(--bp-warning)', fontWeight: 500 }}>他の変更と競合しました。再読み込みしてください</span>
              )}
              {saveFailed && !saveConflict && (
                <span style={{ fontSize: 11, color: 'var(--bp-critical)', fontWeight: 500 }}>保存に失敗しました</span>
              )}
              {isDirty && !saveFailed && !saveConflict && (
                <span style={{ fontSize: 11, color: 'var(--bp-ink3)' }}>保存中...</span>
              )}
            </div>
          </div>
        </div>

        {/* 一部フェーズの生成失敗バナー */}
        {failedPhases.length > 0 && !failedPhasesDismissed && (
          <div style={{
            padding: '10px 32px',
            background: 'var(--bp-warning-pale)',
            borderBottom: '1px solid var(--bp-warning-border)',
            fontSize: 13,
            color: 'var(--bp-warning)',
            fontWeight: 600,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12
          }}
          >
            <span>
              ⚠ 一部のフェーズ（{failedPhases.map((p) => p.label).join('、')}）の生成に失敗し、空データのまま処理が続行されました。<br />
              内容を確認し、必要に応じて再解析してください。
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {sourceNoteId != null && (
                <button
                  onClick={() => setRetryConfirmOpen(true)}
                  disabled={retrying}
                  style={{
                    padding: '6px 14px',
                    background: 'transparent',
                    border: '1px solid var(--bp-warning-border)',
                    borderRadius: 6,
                    color: 'var(--bp-warning)',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: retrying ? 'not-allowed' : 'pointer',
                    opacity: retrying ? 0.7 : 1,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {retrying ? '再解析中...' : '再解析する'}
                </button>
              )}
              <button
                onClick={() => setFailedPhasesDismissed(true)}
                aria-label='閉じる'
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--bp-warning)',
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: 1,
                  cursor: 'pointer',
                  flexShrink: 0,
                  padding: 2
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* 楽観的ロック衝突バナー */}
        {conflict && (
          <div style={{
            padding: '10px 32px',
            background: 'var(--bp-critical-pale)',
            borderBottom: '1px solid var(--bp-critical-border)',
            fontSize: 13,
            color: 'var(--bp-critical)',
            fontWeight: 600,
            flexShrink: 0
          }}
          >
            ⚠ 別の操作で内容が更新されました。ページを再読み込みしてください。
          </div>
        )}

        {/* タブコンテンツ */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>
          <TabsContent value='concepts'>
            <ConceptsTab
              entities={kg.concept_model?.entities ?? []}
              entityRoleLabels={entityRoleLabels}
              onUpdateEntity={(entityId, changes) => {
                dispatch({ type: 'UPDATE_CONCEPT_ENTITY', entityId, changes })
                triggerSave()
              }}
            />
          </TabsContent>
          <TabsContent value='demands'>
            <DemandsTab
              demands={demands}
              onUpdateDemand={(demandId, changes) => {
                dispatch({ type: 'UPDATE_DEMAND', demandId, changes })
                triggerSave()
              }}
            />
          </TabsContent>
          <TabsContent value='requirements'>
            <RequirementsTab
              requirements={requirements}
              demands={demands}
              onUpdateRequirement={(requirementId, changes) => {
                dispatch({ type: 'UPDATE_REQUIREMENT', requirementId, changes })
                triggerSave()
              }}
            />
          </TabsContent>
          <TabsContent value='flows'>
            <FlowsTab
              flows={kg.business_flows ?? []}
              lanes={businessFlowLanes}
              entities={kg.concept_model?.entities ?? []}
              viewMode={flowViewMode}
              onViewModeChange={setFlowViewMode}
              onUpdateStep={(businessFlowId, stepId, changes) => {
                dispatch({ type: 'UPDATE_BUSINESS_FLOW_STEP', businessFlowId, stepId, changes })
                triggerSave()
              }}
            />
          </TabsContent>
          <TabsContent value='matrix'>
            <MatrixTab matrix={kg.permission_matrix} />
          </TabsContent>
          <TabsContent value='questions'>
            <QuestionsTab questions={unresolvedQuestions ?? []} />
          </TabsContent>
        </div>
      </Tabs>

      {/* 確定確認ダイアログ */}
      <AlertDialog open={confirmOpen} onOpenChange={(next) => { if (!next) setConfirmOpen(false) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>品質チェックを依頼しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              現在の要件定義の内容で品質チェックを開始します。完了まで数分かかる場合があります。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={confirming}>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleConfirm() }}
              disabled={confirming}
              className='bg-(--bp-accent) text-white border-transparent hover:bg-(--bp-accent-dim)'
            >
              依頼する
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 再解析確認ダイアログ */}
      <AlertDialog open={retryConfirmOpen} onOpenChange={(next) => { if (!next) setRetryConfirmOpen(false) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>失敗したフェーズを再解析しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              失敗したフェーズ以降の内容を再生成します。当該フェーズ以降を手動で編集していた場合、その変更は上書きされます。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={retrying}>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleRetryAnalyze() }}
              disabled={retrying}
              className='bg-(--bp-accent) text-white border-transparent hover:bg-(--bp-accent-dim)'
            >
              再解析する
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  )
}
